import {
  Box,
  Button,
  Card,
  DataDisplay,
  formatBoolean,
  formatDate,
  formatSchoolYear,
  Grid,
  GridBreak,
  MenuButton,
  pn,
  Stack,
  useElementBreakpoints,
} from "@compass/core";
import { Select } from "components";
import { AprUbResponseRow, LkStudentDisplayFieldId, LkStudentEnrollmentDisplayFieldId, StudentAprUbResource } from "generated/openApi";
import "moment-timezone";
import { useEffect, useMemo, useState } from "react";
import ApiService from "services/apiService";
import SpellcheckIcon from '@mui/icons-material/Spellcheck';

export const StudentAprUb = (props: { initialLoad: boolean; studentId: number; refreshKey?: number }) => {
  const { breakpointsRef, smallFieldBreakpoints, mediumFieldBreakpoints, largeFieldBreakpoints, fullFieldBreakpoints } =
    useElementBreakpoints();

  // const navigate = useNavigate();
  // const { matchTemplatePath } = useRouter();
  const [apr, setApr] = useState<AprUbResponseRow>({} as AprUbResponseRow);
  const [resource, setResource] = useState<StudentAprUbResource>({} as StudentAprUbResource);
  const [selectedSchoolyear, setSelectedSchoolyear] = useState<number>();
  const [schoolYears, setSchoolYears] = useState<number[]>([]);

  useEffect(() => {
    if (props.initialLoad) {
      fetchData();
    }
  }, [props.initialLoad, props.studentId]);

  useEffect(() => {
    if (selectedSchoolyear) {
      ApiService.students.getAprUb(props.studentId, selectedSchoolyear).then((response) => {
        setApr(response);
      });
    }
  }, [selectedSchoolyear]);

  useEffect(() => {
    if (props.initialLoad && selectedSchoolyear) {
      ApiService.students.getAprUb(props.studentId, selectedSchoolyear).then((response) => {
        setApr(response);
      });
    }
  }, [props.refreshKey]);

  const fetchData = async () => {

    const ubResource = await ApiService.students.getAprUbResource();
    if (ubResource) {
      setResource(ubResource);
    }

    const studentEnrollments = await ApiService.studentEnrollments.getEnrollmentsByStudentId(props.studentId);
    if (studentEnrollments && studentEnrollments.length > 0) {
      const years = studentEnrollments.map((enrollment) => enrollment.year);
      setSchoolYears(years);
      setSelectedSchoolyear(years[0]);
    } 
  };

  const properties = resource.properties || [];

  const studentDisplayFields = resource.studentDisplayFields ?? [];
  const atRiskStdTestDisplayField = studentDisplayFields.find(
    (field) => field.id === LkStudentDisplayFieldId.AtRiskStdTest
  );
  const atRiskAlgebraDisplayField = studentDisplayFields.find(
    (field) => field.id === LkStudentDisplayFieldId.AtRiskAlgebra
  );
  const atRiskLowGpaDisplayField = studentDisplayFields.find(
    (field) => field.id === LkStudentDisplayFieldId.AtRiskLowGpa
  );
  const otherNeedDisplayField = studentDisplayFields.find((field) => field.id === LkStudentDisplayFieldId.OtherNeed);
  const disconnectedDisplayField = studentDisplayFields.find(
    (field) => field.id === LkStudentDisplayFieldId.Disconnected
  );

  // const properties = resource.properties || [];

  const lepInstructionDisplayField = studentDisplayFields.find((field) => field.id === LkStudentEnrollmentDisplayFieldId.LEPInstruction);

  const eligibilityField = () => {
    const propertyName = pn<AprUbResponseRow>("eligibility");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay
      // label={property?.displayName}
      label="16.EligibilityCD"
    >{apr.eligibility}</DataDisplay>;
  };

  const genderCodeField = () => {
    const propertyName = pn<AprUbResponseRow>("genderCode");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`9.${property?.displayName}`}>{apr.genderCode}</DataDisplay>;
  };

  const dateOfBirthField = () => {
    const propertyName = pn<AprUbResponseRow>("dateOfBirth");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`8.${property?.displayName}`}>{formatDate(apr.dateOfBirth)}</DataDisplay>;
  };

  const ethnicityField = () => {
    const propertyName = pn<AprUbResponseRow>("ethnicityCode");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`10.${property?.displayName}`}>{apr.ethnicityCode}</DataDisplay>;
  };

  const isRaceNativeField = () => {
    const propertyName = pn<AprUbResponseRow>("isRaceNative");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`11.${property?.displayName}`}>{apr.isRaceNative}</DataDisplay>;
  };

  const isRaceAsianField = () => {
    const propertyName = pn<AprUbResponseRow>("isRaceAsian");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`12.${property?.displayName}`}>{apr.isRaceAsian}</DataDisplay>;
  };

  const isRaceAfricanField = () => {
    const propertyName = pn<AprUbResponseRow>("isRaceAfrican");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`13.${property?.displayName}`}>{apr.isRaceAfrican}</DataDisplay>;
  };

  const isRaceWhiteField = () => {
    const propertyName = pn<AprUbResponseRow>("isRaceWhite");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`14.${property?.displayName}`}>{apr.isRaceWhite}</DataDisplay>;
  };

  const isRacePacificField = () => {
    const propertyName = pn<AprUbResponseRow>("isRacePacific");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`15.${property?.displayName}`}>{apr.isRacePacific}</DataDisplay>;
  };

  const lepEntryField = () => {
    const propertyName = pn<AprUbResponseRow>("lepEntry");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`20.${property?.displayName}`}>{apr.lepEntry}</DataDisplay>;
  };

  const lepInstructionField = () => {
    const propertyName = pn<AprUbResponseRow>("lepInstruction");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`51.${property?.displayName}`}>{apr.lepInstruction}</DataDisplay>;
  };

  const deceasedField = () => {
    const propertyName = pn<AprUbResponseRow>("deceased");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`26.${property?.displayName}`}>{apr.deceased}</DataDisplay>;
  };

  const ncesSchoolField = () => {
    const propertyName = pn<AprUbResponseRow>("ncesSchool");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`23.${property?.displayName}`}>{apr.ncesSchool}</DataDisplay>;
  };

  const collegeGradeLevelField = () => {
    const propertyName = pn<AprUbResponseRow>("collegeGradeLevel");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`25.${property?.displayName}`}>{apr.collegeGradeLevel}</DataDisplay>;
  };

  const projectEntryDateField = () => {
    const propertyName = pn<AprUbResponseRow>("projectEntryDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`24.${property?.displayName}`}>{apr.projectEntryDate}</DataDisplay>;
  };

  const participantStatusField = () => {
    const propertyName = pn<AprUbResponseRow>("participantStatus");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`27.${property?.displayName}`}>{apr.participantStatus}</DataDisplay>;
  };

  const participantLevelField = () => {
    const propertyName = pn<AprUbResponseRow>("participantLevel");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`28.${property?.displayName}`}>{apr.participantLevel}</DataDisplay>;
  };

  const federalServedField = () => {
    const propertyName = pn<AprUbResponseRow>("federalServed");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`29.${property?.displayName}`}>{apr.federalServed}</DataDisplay>;
  };

  const startGradeLevelField = () => {
    const propertyName = pn<AprUbResponseRow>("startGradeLevel");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`30.${property?.displayName}`}>{apr.startGradeLevel}</DataDisplay>;
  };

  const endGradeLevelField = () => {
    const propertyName = pn<AprUbResponseRow>("endGradeLevel");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`31.${property?.displayName}`}>{apr.endGradeLevel}</DataDisplay>;
  };

  const highSchoolGPAField = () => {
    const propertyName = pn<AprUbResponseRow>("highSchoolGPA");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`32.${property?.displayName}`}>{apr.highSchoolGPA}</DataDisplay>;
  };

  const highSchoolGPAUnweightedField = () => {
    const propertyName = pn<AprUbResponseRow>("highSchoolGPAUnweighted");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`33.${property?.displayName}`}>{apr.highSchoolGPAUnweighted}</DataDisplay>;
  };

  const highSchoolGraduateField = () => {
    const propertyName = pn<AprUbResponseRow>("highSchoolGraduate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`34.${property?.displayName}`}>{apr.highSchoolGraduate}</DataDisplay>;
  };

  const highSchoolGraduateDateField = () => {
    const propertyName = pn<AprUbResponseRow>("highSchoolGraduateDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`35.${property?.displayName}`}>{apr.highSchoolGraduateDate}</DataDisplay>;
  };

  const rigorousStudyField = () => {
    const propertyName = pn<AprUbResponseRow>("rigorousStudy");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`36.${property?.displayName}`}>{apr.rigorousStudy}</DataDisplay>;
  };

  const hsProficientRlaField = () => {
    const propertyName = pn<AprUbResponseRow>("hsProficientRla");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`38.${property?.displayName}`}>{apr.hsProficientRla}</DataDisplay>;
  };

  const hsProficientMathField = () => {
    const propertyName = pn<AprUbResponseRow>("hsProficientMath");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`39.${property?.displayName}`}>{apr.hsProficientMath}</DataDisplay>;
  };

  const apCourseField = () => {
    const propertyName = pn<AprUbResponseRow>("apCourse");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`40.${property?.displayName}`}>{apr.apCourse}</DataDisplay>;
  };

  const dualEnrollParticipationField = () => {
    const propertyName = pn<AprUbResponseRow>("dualEnrollParticipation");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`41.${property?.displayName}`}>{apr.dualEnrollParticipation}</DataDisplay>;
  };

  const dualEnrollCredentials = () => {
    const propertyName = pn<AprUbResponseRow>("dualEnrollCredentials");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`42.${property?.displayName}`}>{apr.dualEnrollCredentials}</DataDisplay>;
  };

  const advancedMathField = () => {
    const propertyName = pn<AprUbResponseRow>("advancedMath");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`43.${property?.displayName}`}>{apr.advancedMath}</DataDisplay>;
  };

  const fafsaField = () => {
    const propertyName = pn<AprUbResponseRow>("fafsa");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`44.${property?.displayName}`}>{apr.fafsa}</DataDisplay>;
  };

  const lastServiceDateField = () => {
    const propertyName = pn<AprUbResponseRow>("lastServiceDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`45.${property?.displayName}`}>{apr.lastServiceDate}</DataDisplay>;
  };

  const reasonField = () => {
    const propertyName = pn<AprUbResponseRow>("reason");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`46.${property?.displayName}`}>{apr.reason}</DataDisplay>;
  };

  const workStudyField = () => {
    const propertyName = pn<AprUbResponseRow>("workStudy");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`47.${property?.displayName}`}>{apr.workStudy}</DataDisplay>;
  };

  const employmentField = () => {
    const propertyName = pn<AprUbResponseRow>("employment");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`48.${property?.displayName}`}>{apr.employment}</DataDisplay>;
  };

  const culturalField = () => {
    const propertyName = pn<AprUbResponseRow>("cultural");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`49.${property?.displayName}`}>{apr.cultural}</DataDisplay>;
  };

  const communityServiceField = () => {
    const propertyName = pn<AprUbResponseRow>("communityService");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`50.${property?.displayName}`}>{apr.communityService}</DataDisplay>;
  };

  const selfTranCDField = () => {
    const propertyName = pn<AprUbResponseRow>("selfTranCD");
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`52.${property?.displayName}`}>{apr.selfTranCD}</DataDisplay>;
  };

  const atRiskStdTestField = () => {
    return <DataDisplay
      label="17.AtRiskStdTest"
    >{apr.atRiskStdTest}</DataDisplay>;
  };

  const atRiskAlgebraField = () => {
    return <DataDisplay
      label="19.AtRiskAlgebra"
    >{apr.atRiskAlgebra}</DataDisplay>;
  };

  const atRiskLowGpaField = () => {
    return <DataDisplay
      label="18.AtRiskLowGpa"
    >{apr.atRiskLowGPA}</DataDisplay>;
  };

  const otherNeedField = () => {
    return <DataDisplay
      label="22.OtherNeed"
    >{apr.otherNeed}</DataDisplay>;
  };

  const disconnectedField = () => {
    return <DataDisplay
      label="21.Disconnected"
    >{apr.disconnected}</DataDisplay>;
  };

  // const schoolYearSelect = () => {
  //   return (
  //     <Select
  //       label="School Year"
  //       value={selectedSchoolyear}
  //       options={schoolYears}
  //       onChange={(e, value) => setSelectedSchoolyear(value)}
  //       getOptionLabel={(year: number) => formatSchoolYear(year)!}
  //       renderOption={(props, year) => <li {...props}>{formatSchoolYear(year)}</li>}
  //     />
  //   );
  // };

  const schoolYearSelect = () => {
    const selectedValue =
      schoolYears.find((x) => x === selectedSchoolyear) ?? undefined;

    return (
      <Box pt={1}>
        <Grid container spacing="md" alignItems="center">
          <Grid item xs={12} md={2}>
            <Select
              label="School Year"
              required
              value={selectedValue}
              options={schoolYears}
              getOptionLabel={(year: number) => formatSchoolYear(year)!}
              renderOption={(props, year: number) => {
                return (
                  <li {...props}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      width="100%"
                      sx={{
                        fontWeight: year === selectedSchoolyear ? 600 : 400,
                        color:
                          year === selectedSchoolyear
                            ? "primary.main"
                            : "text.primary",
                      }}
                    >
                      {formatSchoolYear(year)}

                      {year === selectedSchoolyear && (
                        <Box
                          sx={{
                            ml: 1,
                            px: 1,
                            borderRadius: "8px",
                            fontSize: "0.7rem",
                            color: "primary.main",
                          }}
                        >
                          {/* <SpellcheckIcon fontSize="small" /> */}
                        </Box>
                      )}
                    </Box>
                  </li>
                );
              }}
              onChange={(e, value) =>
                setSelectedSchoolyear(value ?? undefined)
              }
              size="small"
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

  // const enrollmentParticipantStatus = () => {
  //   const enrollment = apr.enrollments?.find((x) => x.schoolYear === selectedSchoolyear);
  //   return <DataDisplay label="Participant Status">{enrollment?.participantStatus}</DataDisplay>;
  // };

  //  

  return (
    <Card>
      <Grid sx={{ pb: 2 }}>
        {schoolYearSelect()}
      </Grid>
      <Grid container spacing="md">
        <Grid
          item
          xs={12}
          lg={12}
          sx={{
            pr: { md: "10px", xs: 0 },
            pb: { xs: "10px", md: 0 },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              borderRadius: 3,
              border: "1px solid #ddd",
              overflow: "hidden",
              background: "#fff",
              flexGrow: 1,
            }}
          >
            <Box
              sx={{
                background: "#0d57a1",
                color: "white",
                px: 2,
                py: 1,
                fontSize: "20px",
                fontWeight: 600,
              }}
            >
              Participant's Demographic Information
            </Box>

            <Box sx={{ p: 3 }}>
              <Grid container spacing="sm">

                <Grid item xs={12} md={4}>
                  {dateOfBirthField()}
                </Grid>

                <Grid item xs={12} md={4}>
                  {genderCodeField()}
                </Grid>

                <Grid item xs={12} md={4}>
                  {ethnicityField()}
                </Grid>

                <Grid item xs={12} md={4}>
                  {isRaceNativeField()}
                </Grid>

                <Grid item xs={12} md={4}>
                  {isRaceAsianField()}
                </Grid>

                <Grid item xs={12} md={4}>
                  {isRaceAfricanField()}
                </Grid>

                <Grid item xs={12} md={4}>
                  {isRaceWhiteField()}
                </Grid>

                <Grid item xs={12} md={4}>
                  {isRacePacificField()}
                </Grid>

              </Grid>
            </Box>

          </Box>
        </Grid>
        {/* Eligibility and Initial Selection SECTION */}
        <Grid item xs={12} lg={12} sx={{
          pr: { md: "10px", xs: 0 },
          pb: { xs: "10px", md: 0 },
          display: "flex",
          flexDirection: "column",
        }}>
          <Stack direction="column" sx={{ gap: 2 }}>
            <Box
              sx={{
                borderRadius: 3,
                border: "1px solid #ddd",
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <Box
                sx={{
                  background: "#0d57a1",
                  color: "white",
                  px: 2,
                  py: 1,
                  fontSize: "20px",
                  fontWeight: 600,
                }}
              >
                Eligibility and Initial Selection
              </Box>

              <Box sx={{ p: 3 }}>
                <Grid container spacing="sm">
                  {/* <Grid item xs={12} md={6}>
                    {schoolYearSelect()}
                  </Grid> */}

                  {/* <Grid item xs={12} md={4}>
                    {enrollmentParticipantStatus()}
                  </Grid> */}

                  <Grid item xs={12} md={4}>
                    {eligibilityField()}
                  </Grid>

                  {atRiskStdTestDisplayField && (
                    <Grid item xs={12} md={4}>
                      {atRiskStdTestField()}
                    </Grid>
                  )}

                  {atRiskLowGpaDisplayField && (
                    <Grid item xs={12} md={4}>
                      {atRiskLowGpaField()}
                    </Grid>
                  )}

                  {atRiskAlgebraDisplayField && (
                    <Grid item xs={12} md={4}>
                      {atRiskAlgebraField()}
                    </Grid>
                  )}

                  <Grid item xs={12} md={4}>
                    {lepEntryField()}
                  </Grid>

                  {disconnectedDisplayField && (
                    <Grid item xs={12} md={4}>
                      {disconnectedField()}
                    </Grid>
                  )}

                  {otherNeedDisplayField && (
                    <Grid item xs={12} md={4}>
                      {otherNeedField()}
                    </Grid>
                  )}

                </Grid>
              </Box>
            </Box>
          </Stack>
        </Grid>

       

        {/* Participation and Status in secondary school SECTION */}
        <Grid item xs={12} lg={12} sx={{
          pr: { md: "10px", xs: 0 },
          pb: { xs: "10px", md: 0 },
          display: "flex",
          flexDirection: "column",
        }}>
          <Stack direction="column" sx={{ gap: 2 }}>
            <Box
              sx={{
                borderRadius: 3,
                border: "1px solid #ddd",
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <Box
                sx={{
                  background: "#0d57a1",
                  color: "white",
                  px: 2,
                  py: 1,
                  fontSize: "20px",
                  fontWeight: 600,
                }}
              >
                Participation and Status in Secondary School
              </Box>
              <Box sx={{ p: 3 }}>
                <Grid container spacing="sm">

                  <Grid item xs={12} md={4}>
                    {ncesSchoolField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {projectEntryDateField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {collegeGradeLevelField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {deceasedField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {participantStatusField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {participantLevelField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {federalServedField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {startGradeLevelField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {endGradeLevelField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {highSchoolGPAField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {highSchoolGPAUnweightedField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {highSchoolGraduateField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {highSchoolGraduateDateField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {rigorousStudyField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {hsProficientRlaField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {hsProficientMathField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {apCourseField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {dualEnrollParticipationField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {dualEnrollCredentials()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {advancedMathField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {fafsaField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {lastServiceDateField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {reasonField()}
                  </Grid>

                </Grid>
              </Box>
            </Box>
          </Stack>
        </Grid>

         {/* Project Services SECTION */}
        <Grid item xs={12} lg={12} sx={{
          pr: { md: "10px", xs: 0 },
          pb: { xs: "10px", md: 0 },
          display: "flex",
          flexDirection: "column",
        }}>
          <Stack direction="column" sx={{ gap: 2 }}>
            <Box
              sx={{
                borderRadius: 3,
                border: "1px solid #ddd",
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <Box
                sx={{
                  background: "#0d57a1",
                  color: "white",
                  px: 2,
                  py: 1,
                  fontSize: "20px",
                  fontWeight: 600,
                }}
              >
                Project Services
              </Box>

              <Box sx={{ p: 3 }}>
                <Grid container spacing="sm">

                  <Grid item xs={12} md={4}>
                    {lepInstructionField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {workStudyField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {employmentField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {culturalField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {communityServiceField()}
                  </Grid>

                </Grid>
              </Box>
            </Box>
          </Stack>
        </Grid>

        {/* Postsecondary education SECTION */}
        <Grid item xs={12} lg={12} sx={{
          pr: { md: "10px", xs: 0 },
          pb: { xs: "10px", md: 0 },
          display: "flex",
          flexDirection: "column",
        }}>
          <Stack direction="column" sx={{ gap: 2 }}>
            <Box
              sx={{
                borderRadius: 3,
                border: "1px solid #ddd",
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <Box
                sx={{
                  background: "#0d57a1",
                  color: "white",
                  px: 2,
                  py: 1,
                  fontSize: "20px",
                  fontWeight: 600,
                }}
              >
                Postsecondary Education
              </Box>
              <Box sx={{ p: 3 }}>
                <Grid container spacing="sm">

                  <Grid item xs={12} md={4}>
                    {selfTranCDField()}
                  </Grid>

                </Grid>
              </Box>
            </Box>
          </Stack>
        </Grid>

        {/* FOR FUTURE USE SECTION */}
        {/* <Grid item xs={12} lg={12} sx={{
          pr: { md: "10px", xs: 0 },
          pb: { xs: "10px", md: 0 },
          display: "flex",
          flexDirection: "column",
        }}>
          <Stack direction="column" sx={{ gap: 2 }}>
            <Box
              sx={{
                borderRadius: 3,
                border: "1px solid #ddd",
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <Box
                sx={{
                  background: "#0d57a1",
                  color: "white",
                  px: 2,
                  py: 1,
                  fontSize: "20px",
                  fontWeight: 600,
                }}
              >
                FUTURE USE
              </Box>
                            <Box sx={{ p: 3 }}>
                <Grid container spacing="sm">

                  <Grid item xs={12} md={4}>
                    {lepInstructionField()}
                  </Grid>

                </Grid>
              </Box>
            </Box>
          </Stack>
        </Grid> */}

      </Grid>
    </Card>
  )
};
